<template>
    <v-col cols="12">
        <PackageDialog
            :is-open="packageDialog"
            :package-data="currentPackage"
            :box-types-data="boxTypes"
            :package-types-data="packageTypes"
            @on-close="closePackageDialog"
            @on-save="savePackage"
        />

        <ConfirmDialog
            :is-open="confirmDeletePackageDialog"
            :data="currentPackage"
            @on-confirm="deletePackage"
            @on-abort="closeConfirmDeletePackageDialog"
        >
            <template #title>
                Usuwanie pakowania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć pakowanie?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <PackageWareDialog
            :is-open="packageWareDialog"
            :task-data="task"
            :package-ware-data="currentPackageWare"
            :package-data="currentPackage"
            :products-data="productsData"
            @on-close="closePackageWareDialog"
            @on-save="savePackageWare"
        />

        <ConfirmDialog
            :is-open="confirmDeletePackageWareDialog"
            :data="currentPackageWare"
            @on-confirm="deletePackageWare"
            @on-abort="closeConfirmDeletePackageWareDialog"
        >
            <template #title>
                Usuwanie produktu z pakowania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć produkt?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-btn
            v-if="isCalculationEditable"
            color="secondary"
            class="mb-2"
            depressed
            :disabled="!taskData.isSelected"
            data-sentry-label="Dodaj pakowanie"
            @click="showPackageDialog(null)"
        >
            <v-icon left>
                mdi-plus
            </v-icon>
            Dodaj pakowanie
        </v-btn>

        <v-data-table
            :items="task.calculationImpositionTaskPackages"
            :headers="isCalculationEditable ? packagesHeaders : readOnlyPackageHeaders"
            disable-pagination
            :mobile-breakpoint="0"
            hide-default-footer
            show-expand
            item-key="index"
            :expanded.sync="expanded"
        >
            <template #item.boxType="{item}">
                <span :class="{'error--text': packageErrors(item)}">
                    {{ item.boxType.name }}
                </span>
            </template>
            <template #item.packageType="{item}">
                {{ item.packageType.name }}
                <v-tooltip :open-delay="tooltipDelay" top>
                    <template #activator="{on}">
                        <v-icon color="primary" size="20" class="ml-2" v-on="on">
                            mdi-axis-arrow-info
                        </v-icon>
                    </template>
                    <span>
                        D: {{ item.length }} <br>
                        SZ: {{ item.width }} <br>
                        W: {{ item.height }}
                    </span>
                </v-tooltip>
            </template>

            <template #item.amount="{item}">
                {{ parseEmptyValue(item.packageAmount) }}
            </template>
            <template #item.maxWeightCapacity="{item}">
                {{ formatWeight(item.currentMaxWeightCapacity) }}
            </template>
            <template #item.actions="{item}">
                <div class="d-inline-flex">
                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                :disabled="!taskData.isSelected"
                                data-sentry-label="Edycja pakowania"
                                v-on="on"
                                @click="showPackageDialog(item)"
                            >
                                <v-icon color="blue">
                                    mdi-pencil
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Edycja</span>
                    </v-tooltip>
                    <v-tooltip :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <v-btn
                                icon
                                :disabled="!taskData.isSelected"
                                data-sentry-label="Potwierdzenie usunięcia pakowania"
                                v-on="on"
                                @click="showConfirmDeletePackageDialog(item)"
                            >
                                <v-icon color="pink">
                                    mdi-delete
                                </v-icon>
                            </v-btn>
                        </template>
                        <span>Usuń</span>
                    </v-tooltip>
                </div>
            </template>
            <template #expanded-item="{headers, item}">
                <td :colspan="headers.length">
                    <v-container>
                        <v-row align="center">
                            <v-col cols="auto">
                                <strong>Produkty</strong>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn
                                    v-if="isCalculationEditable"
                                    color="secondary"
                                    fab
                                    x-small
                                    class="elevation-1"
                                    :disabled="!taskData.isSelected"
                                    data-sentry-label="Dodaj produkty"
                                    @click="showPackageWareDialog(item)"
                                >
                                    <v-icon>
                                        mdi-plus
                                    </v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-data-table
                                    :items="item.calculationImpositionTaskPackageWares"
                                    :headers="isCalculationEditable ? packageProductsHeaders : readOnlyPackageProductsHeaders"
                                    disable-pagination
                                    :mobile-breakpoint="0"
                                    item-key="index"
                                    hide-default-footer
                                >
                                    <template #item.productName="props">
                                        {{ props.item.calculationProduct.name }}
                                    </template>
                                    <template #item.productAmount="props">
                                        {{ props.item.amount }} / {{ props.item.calculationProduct.amount }}
                                    </template>
                                    <template #item.weight="props">
                                        {{ formatWeight(roundWeight(props.item.totalWeight)) }}
                                    </template>
                                    <template #item.actions="props">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        :disabled="!taskData.isSelected"
                                                        data-sentry-label="Edycja produktu w pakowaniu"
                                                        v-on="on"
                                                        @click="showPackageWareDialog(item, props.item)"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Edycja</span>
                                            </v-tooltip>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        :disabled="!taskData.isSelected"
                                                        data-sentry-label="Potwierdzenie usunięcia produktu z pakowania"
                                                        v-on="on"
                                                        @click="showConfirmDeletePackageWareDialog(item, props.item)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń</span>
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-data-table>
                            </v-col>
                        </v-row>
                    </v-container>
                </td>
            </template>
        </v-data-table>
    </v-col>
</template>

<script>
import { appConfig } from '@/config'
import parse from '@/mixins/parse'
import PackageDialog from './PackageDialog'
import PackageWareDialog from './PackageWareDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import { errorHandler } from '@/utils/errorHandler'

import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

export default {
    name: 'Packing',
    components: { PackageDialog, PackageWareDialog, ConfirmDialog },
    mixins: [ tooltipDelay, weightFormatter, parse ],
    props: {
        isCalculationEditable: {
            type: Boolean
        },
        taskData: {
            type: Object,
            default: () => {}
        },
        productsData: {
            type: Array,
            default: () => []
        },
        validations: {
            type: Object,
            default: () => {}
        }
    },
    data() {
        return {
            task: {},
            currentPackage: null,
            packageDialog: false,
            confirmDeletePackageDialog: false,
            currentPackageWare: null,
            packageWareDialog: false,
            confirmDeletePackageWareDialog: false,
            boxTypes: [],
            packageTypes: [],
            expanded: [],
            packagesHeaders: [
                {
                    text: 'Sposób pakowania',
                    value: 'boxType',
                    sortable: false,
                    width: 150,
                    showOnRead: true
                },
                {
                    text: 'Rodzaj opakowania',
                    value: 'packageType',
                    sortable: false,
                    showOnRead: true
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 80,
                    showOnRead: true
                },
                {
                    text: 'Nośność',
                    value: 'maxWeightCapacity',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    showOnRead: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 100,
                    showOnRead: false
                },
                {
                    text: '',
                    value: 'data-table-expand',
                    showOnRead: true
                }
            ],
            packageProductsHeaders: [
                {
                    text: 'Produkt',
                    value: 'productName',
                    sortable: false,
                    showOnRead: true
                },
                {
                    text: 'Nakład',
                    value: 'productAmount',
                    sortable: false,
                    align: 'center',
                    width: 200,
                    showOnRead: true
                },
                {
                    text: 'Waga',
                    value: 'weight',
                    sortable: false,
                    align: 'center',
                    width: 120,
                    showOnRead: true
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    align: 'center',
                    sortable: false,
                    width: 120,
                    showOnRead: false
                },
            ]
        }
    },
    computed: {
        readOnlyPackageHeaders() {
            return this.packagesHeaders.filter(item => item.showOnRead)
        },
        readOnlyPackageProductsHeaders() {
            return this.packageProductsHeaders.filter(item => item.showOnRead)
        },
        isCarrierPalletTransport() {
            return this.taskData.selectedTransport === appConfig.shipping.carrierPalletTransport
        },
    },
    watch: {
        taskData: {
            handler: function(newValue) {
                this.task = JSON.parse(JSON.stringify(newValue))
                this.indexCalculationImpositionTaskPackages(this.task)
            },
            deep: true
        }
    },
    mounted() {
        this.taskDataHandler()
    },
    methods: {
        taskDataHandler() {
            const task = JSON.parse(JSON.stringify(this.taskData))

            this.indexCalculationImpositionTaskPackages(task)

            this.task = task
        },
        showPackageDialog(packageData) {
            this.currentPackage = packageData

            Promise.all([
                this.$store.dispatch('calculations/getPackageBoxTypes'),
                this.$store.dispatch('calculations/getPackagesTypes')
            ])
                .then(responses => {
                    this.boxTypes = responses[0].data.results
                    this.packageTypes = responses[1].data.results
                    this.packageDialog = true
                })
                .catch(error => errorHandler(error))
        },
        closePackageDialog() {
            setTimeout(() => {
                this.currentPackage = null
            }, 500)
            this.packageDialog = false
        },
        savePackage(packageData, isNewPackage) {
            this.packageDialog = false

            if (!isNewPackage) {
                const index = this.findPackageIndex(packageData)

                this.task.calculationImpositionTaskPackages[index] = packageData
            } else {
                this.task.calculationImpositionTaskPackages.push(packageData)
            }

            this.recalculatePackageAmount(packageData)
            this.indexCalculationImpositionTaskPackages(this.task)

            this.closePackageDialog()
        },
        showConfirmDeletePackageDialog(packageData) {
            this.currentPackage = packageData
            this.confirmDeletePackageDialog = true
        },
        closeConfirmDeletePackageDialog() {
            setTimeout(() => {
                this.currentPackage = null
            }, 500)
            this.confirmDeletePackageDialog = false
        },
        deletePackage(packageData) {
            const index = this.findPackageIndex(packageData)

            this.task.calculationImpositionTaskPackages.splice(index, 1)

            this.indexCalculationImpositionTaskPackages(this.task)

            this.$emit('save-task-packages', this.task)

            this.closeConfirmDeletePackageDialog()
        },
        indexCalculationImpositionTaskPackages(task) {
            task.calculationImpositionTaskPackages = task.calculationImpositionTaskPackages.map((item, index) => ({
                ...item,
                index
            }))
        },
        findPackageIndex(packageData) {
            return this.task.calculationImpositionTaskPackages.findIndex(item => {
                return (typeof item.id !== 'undefined' && item.id === packageData.id) ||
                    (typeof item.index !== 'undefined' && item.index === packageData.index)
            })
        },
        showPackageWareDialog(packageData, packageWareData = null) {
            this.currentPackage = packageData
            this.currentPackageWare = packageWareData
            this.packageWareDialog = true
        },
        closePackageWareDialog() {
            setTimeout(() => {
                this.currentPackage = null
                this.currentPackageWare = null
            }, 500)
            this.packageWareDialog = false
        },
        savePackageWare(packageData, packageWareData, isNewPackageWare) {
            const packageIndex = this.findPackageIndex(packageData)

            if (!isNewPackageWare) {
                const packageWareIndex = this.findPackageWareIndex(packageIndex, packageWareData)

                this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares[packageWareIndex] = packageWareData
            } else {
                this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares.push(packageWareData)
            }

            this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares = this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares.map((item, index) => ({
                ...item,
                index
            }))

            this.recalculatePackageAmount(this.currentPackage)

            this.closePackageWareDialog()
        },
        showConfirmDeletePackageWareDialog(packageData, packageWareData) {
            this.currentPackage = packageData
            this.currentPackageWare = packageWareData
            this.confirmDeletePackageWareDialog = true
        },
        closeConfirmDeletePackageWareDialog() {
            setTimeout(() => {
                this.currentPackage = null
                this.currentPackageWare = null
            }, 500)
            this.confirmDeletePackageWareDialog = false
        },
        deletePackageWare(packageWareData) {
            const packageIndex = this.findPackageIndex(this.currentPackage)
            const packageWareIndex = this.findPackageWareIndex(packageIndex, packageWareData)

            this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares.splice(packageWareIndex, 1)
            this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares = this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares.map((item, index) => ({
                ...item,
                index
            }))

            this.recalculatePackageAmount(this.currentPackage)

            this.closeConfirmDeletePackageWareDialog()
        },
        findPackageWareIndex(packageIndex, packageWareData) {
            return this.task.calculationImpositionTaskPackages[packageIndex].calculationImpositionTaskPackageWares.findIndex(item => {
                return (typeof item.id !== 'undefined' && item.id === packageWareData.id) ||
                    (typeof item.index !== 'undefined' && item.index === packageWareData.index)
            })
        },
        recalculatePackageAmount(packageData) {
            const payload = {
                packageTypeId: packageData.packageType.id,
                carrierTransport: this.isCarrierPalletTransport,
                goods: packageData.calculationImpositionTaskPackageWares.map(item => ({
                    id: item.calculationProduct.id,
                    amount: parseInt(item.amount),
                    length: this.getValueByProductId(item.calculationProductId, 'formatA'),
                    width: this.getValueByProductId(item.calculationProductId, 'formatB'),
                    paperWeight: this.getValueByProductId(item.calculationProductId, 'paper')?.weight ?? 0,
                    paperThickness: this.getValueByProductId(item.calculationProductId, 'paper')?.thickness ?? 0,
                    varnishUv: this.getValueByProductId(item.calculationProductId, 'varnishUv') ?? false,
                }))
            }

            this.$store.dispatch('calculations/palletQuantityCalculator', payload)
                .then(response => {
                    packageData.packageAmount = response.data.packageAmount
                    packageData.currentMaxWeightCapacity = response.data.packageTypeWeightCapacity
                    
                    if (response.data.goods && response.data.goods.length) {
                        packageData.calculationImpositionTaskPackageWares.forEach((ware, index) => {
                            if (index < response.data.goods.length && response.data.goods[index].weight) {
                                ware.totalWeight = response.data.goods[index].weight
                            }
                        })
                    }

                    this.indexCalculationImpositionTaskPackages(this.task)

                    this.$nextTick(() => {
                        this.$emit('save-task-packages', this.task)
                    })
                })
                .catch(error => errorHandler(error))
        },
        getValueByProductId(productId, param = 'formatA') {
            const product = this.productsData.find(item => item.id === productId)

            return product?.[param] ?? null
        },
        packageErrors(packageData) {
            if (!this.isCalculationEditable) return
            const packageIndex = packageData.index

            return this.validations.$each.$iter[packageIndex]?.$invalid
        }
    }
}
</script>
